import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import isNumber from "lodash/isNumber";
import isObject from "lodash/isObject";
import isString from "lodash/isString";
import isNull from "lodash/isNull";
import { OPERATIONS } from "@/config/const";
import fireStoreService from "../fireStoreService";

const operationReportApi = AiseaApiFactory.get("operationReport");

// value_json（構造化値: 14/15/17/18）に中身があるか判定する
const hasValueJsonContent = value => {
  if (value == null) return false;
  if (isString(value)) return value !== "" && value !== "null";
  if (Array.isArray(value)) return value.length > 0;
  if (isObject(value)) return Object.keys(value).length > 0;
  return false;
};

class OperationReportService {
  // Operationのレポート一覧データを取得
  async getOperationReports(
    payload = this.operationReportIndexRequestObject()
  ) {
    return await operationReportApi.index(payload);
  }

  // Operationのレポートデータを取得
  async getOperationReport(
    routeParams // {aoEnterpriseId: string, reportId: string}
  ) {
    const rp = routeParams;
    const isValidRouteParam = (rp = {}) => {
      return (
        isObject(rp) &&
        isString(rp.aoEnterpriseId) &&
        rp.aoEnterpriseId.length > 0 &&
        isString(rp.reportId) &&
        rp.reportId.length > 0
      );
    };
    if (!isValidRouteParam(rp)) {
      throw new Error("Invalid route parameters");
    }
    return await operationReportApi.show(rp.aoEnterpriseId, rp.reportId);
  }

  // Operationのレポートデータを削除
  async deleteOperationReport(
    routeParams // {aoEnterpriseId: string, reportId: string}
  ) {
    const rp = routeParams;
    const isValidRouteParam = (rp = {}) => {
      return (
        isObject(rp) &&
        isString(rp.aoEnterpriseId) &&
        rp.aoEnterpriseId.length > 0 &&
        isString(rp.reportId) &&
        rp.reportId.length > 0
      );
    };
    if (!isValidRouteParam(rp)) {
      throw new Error("Invalid route parameters");
    }
    return await operationReportApi.delete(rp.aoEnterpriseId, rp.reportId);
  }

  // Operationのレポートのトランザクション実行
  async transactionOperationReport(
    routeParams, // {aoEnterpriseId: string; reportId: string; transactionId: string;}
    body // {version: number}
  ) {
    const rp = routeParams;
    const isValidRouteParam = (rp = {}) => {
      return (
        isObject(rp) &&
        isString(rp.aoEnterpriseId) &&
        rp.aoEnterpriseId.length > 0 &&
        isString(rp.reportId) &&
        rp.reportId.length > 0 &&
        isString(rp.transactionId) &&
        rp.transactionId.length > 0
      );
    };
    const isValidBody = (body = {}) => {
      return isObject(body) && isNumber(body.version);
    };
    if (!isValidRouteParam(rp)) {
      throw new Error("Invalid route parameters");
    }
    if (!isValidBody(body)) {
      throw new Error("Invalid request body");
    }
    return await operationReportApi.transaction(
      rp.aoEnterpriseId,
      rp.reportId,
      rp.transactionId,
      body
    );
  }

  // Operationのレポートのcsvダウンロード
  async downloadOperationReportCsv(
    routeParams // {aoEnterpriseId: string}
  ) {
    const rp = routeParams;
    const isValidRouteParam = (rp = {}) => {
      return (
        isObject(rp) &&
        isString(rp.aoEnterpriseId) &&
        rp.aoEnterpriseId.length > 0
      );
    };
    if (!isValidRouteParam(rp)) {
      throw new Error("Invalid route parameters");
    }
    return await operationReportApi.downloadCsv(rp.aoEnterpriseId, rp.reportId);
  }

  // 一時ファイル作成
  async uploadTempFiles(
    routeParams, // {aoEnterpriseId: string; hash: string;}
    formData //  FormData
  ) {
    const rp = routeParams;
    const isValidRouteParam = (rp = {}) => {
      return (
        isObject(rp) &&
        isString(rp.aoEnterpriseId) &&
        rp.aoEnterpriseId.length > 0 &&
        isString(rp.hash) &&
        rp.hash.length > 0
      );
    };
    const isValidFormData = formData => {
      return formData instanceof FormData;
    };
    if (!isValidRouteParam(rp)) {
      throw new Error("Invalid route parameters");
    }
    if (!isValidFormData(formData)) {
      throw new Error("Invalid request body");
    }
    return await operationReportApi.uploadTempFiles(
      rp.aoEnterpriseId,
      rp.hash,
      formData
    );
  }

  // 特定のCSVファイルをダウンロード
  async downloadReportFile(
    routeParams // {aoEnterpriseId: string; reportId: string; aoEnterpriseCsvId: string | number}
  ) {
    const rp = routeParams;
    const isValidRouteParam = (rp = {}) => {
      return (
        isObject(rp) &&
        isString(rp.aoEnterpriseId) &&
        rp.aoEnterpriseId.length > 0 &&
        isString(rp.reportId) &&
        rp.reportId.length > 0 &&
        (isString(rp.aoEnterpriseCsvId) || isNumber(rp.aoEnterpriseCsvId))
      );
    };
    if (!isValidRouteParam(rp)) {
      throw new Error("Invalid route parameters");
    }
    const res = await operationReportApi.downloadFile(
      rp.aoEnterpriseId,
      rp.reportId,
      rp.aoEnterpriseCsvId
    );

    if (!res || !res.status) {
      throw res;
    }
    return res;
  }

  // レポートデータ更新
  async updateReport(
    routeParams, // {aoEnterpriseId: string; reportId: string;}
    body //  {
    //   "ao_enterprise_vessel_report_form_versions": [
    //     {
    //       "ao_enterprise_vessel_report_form_version_items": [
    //         {
    //           "form_version_item_id": "string",
    //           "nullable_checked": true,
    //           "value_date": "string",
    //           "value_date_time": "2024-04-18T05:00:21.564Z",
    //           "value_files": [
    //             {
    //               "name": "string"
    //             }
    //           ],
    //           "value_number": 0,
    //           "value_string": "string",
    //           "value_time": "string"
    //         }
    //       ],
    //       "form_version_id": "string"
    //     }
    //   ],
    //   "version": 0
    // }
  ) {
    const rp = routeParams;
    const isValidRouteParam = (rp = {}) => {
      return (
        isObject(rp) &&
        isString(rp.aoEnterpriseId) &&
        rp.aoEnterpriseId.length > 0 &&
        isString(rp.reportId) &&
        rp.reportId.length > 0
      );
    };
    if (!isValidRouteParam(rp)) {
      throw new Error("Invalid route parameters");
    }
    return await operationReportApi.update(
      rp.aoEnterpriseId,
      rp.reportId,
      body
    );
  }

  /**
   * レポート一覧を検索するためのqueryのオブジェクトを生成
   */
  operationReportIndexRequestObject({
    accountIds = null, // : string[] | null;
    dateFrom = null, // : string | null; YYYY-MM-DD
    dateTo = null, // : string | null; YYYY-MM-DD
    departmentNames = null, // string[] | null;
    enterpriseVesselIds = null, // string[] | null;
    aoEnterpriseIds = null, // string[] | null;
    page = 0, // number | null
    situations = null, // string[] | null
    size = null, // number | null
    sortColumn = null, // string | null
    sortOrder = null, // string | null
    tz = null, // string | null
  } = {}) {
    const hasItems = queryParameter => {
      return Array.isArray(queryParameter) && queryParameter.length > 0;
    };
    return {
      account_ids: hasItems(accountIds) ? accountIds.join(",") : null,
      date_from: dateFrom,
      date_to: dateTo,
      department_names: hasItems(departmentNames)
        ? departmentNames.join(",")
        : null,
      enterprise_vessel_ids: hasItems(enterpriseVesselIds)
        ? enterpriseVesselIds.join(",")
        : null,
      ao_enterprise_ids: hasItems(aoEnterpriseIds)
        ? aoEnterpriseIds.join(",")
        : null,
      page,
      situations: hasItems(situations) ? situations.join(",") : null,
      size,
      sort_column: sortColumn,
      sort_order: sortOrder,
      tz,
    };
  }

  /**
   * レポート一覧を検索するためのqueryのオブジェクトから元の引数を取得
   */
  getOriginalArgumentsFromRequestObject(returnValue) {
    const rv = returnValue;
    const splitItems = queryParameter => {
      return isString(queryParameter) ? queryParameter.split(",") : null;
    };
    const stringOrNull = queryParameter => {
      return isString(queryParameter) ? queryParameter : null;
    };
    const numberOrNull = queryParameter => {
      return isNumber(queryParameter) ? queryParameter : null;
    };
    return {
      accountIds: splitItems(rv.account_ids),
      dateFrom: stringOrNull(rv.date_from),
      dateTo: stringOrNull(rv.date_to),
      departmentNames: splitItems(rv.department_names),
      enterpriseVesselIds: splitItems(rv.enterprise_vessel_ids),
      aoEnterpriseIds: splitItems(rv.ao_enterprise_ids),
      page: numberOrNull(rv.page),
      situations: splitItems(rv.situations),
      size: numberOrNull(rv.size),
      sortColumn: stringOrNull(rv.sort_column),
      sortOrder: stringOrNull(rv.sort_order),
      tz: stringOrNull(rv.tz),
    };
  }

  validEnterpriseVesselReports(
    operationReports // : AoEnterpriseVesselReportRootList
  ) {
    const or = operationReports;
    if (!this.isValidAoEnterpriseVesselReportRootList(or)) {
      return [];
    }
    return or.ao_enterprise_vessel_reports.filter(evr =>
      this.isValidAoEnterpriseVesselReport(evr)
    );
  }

  /**
   * Retrieves the page number from the given report list.
   *
   * @param {AoEnterpriseVesselReportRootList} operationReports - The report list object.
   * @returns {number} - The page number.
   */
  page(
    operationReports // : AoEnterpriseVesselReportRootList
  ) {
    const or = operationReports;
    if (!this.isValidAoEnterpriseVesselReportRootList(or)) return 0;
    return or.page;
  }

  /**
   * Returns the total number of pages for the given operation reports.
   *
   * @param {AoEnterpriseVesselReportRootList} operationReports - The operation reports to calculate the total pages for.
   * @returns {number} The total number of pages.
   */
  totalPage(
    operationReports // : AoEnterpriseVesselReportRootList
  ) {
    const or = operationReports;
    if (!this.isValidAoEnterpriseVesselReportRootList(or)) return 0;
    return or.total_pages;
  }

  /**
   * Returns the total number of elements in the given operationReports.
   *
   * @param {AoEnterpriseVesselReportRootList} operationReports - The operation reports to calculate the total elements from.
   * @returns {number} The total number of elements in the operationReports.
   */
  totalElements(
    operationReports // : AoEnterpriseVesselReportRootList
  ) {
    const or = operationReports;
    if (!this.isValidAoEnterpriseVesselReportRootList(or)) return 0;
    return or.total_elements;
  }

  /**
   * Checks if the given report list is a valid AoEnterpriseVesselReportRootList.
   *
   * @param {AoEnterpriseVesselReportRootList} operationReports - The report list to validate.
   * @returns {boolean} - Returns true if the report list is valid, false otherwise.
   */
  isValidAoEnterpriseVesselReportRootList(
    operationReports // : AoEnterpriseVesselReportRootList
  ) {
    const or = operationReports;
    if (!Array.isArray(or.ao_enterprise_vessel_reports)) return false;
    return (
      isNumber(or.page) &&
      isNumber(or.total_elements) &&
      isNumber(or.total_pages)
    );
  }

  /**
   * Checks if the given object is a valid AoEnterpriseVesselReport.
   *
   * @param {AoEnterpriseVesselReport} enterpriseVesselReports - The object to validate.
   * @returns {boolean} - Returns true if the object is a valid AoEnterpriseVesselReport, false otherwise.
   */
  isValidAoEnterpriseVesselReport(
    enterpriseVesselReport // : AoEnterpriseVesselReport
  ) {
    const evr = enterpriseVesselReport;
    if (!isObject(evr)) return false;
    return (
      Array.isArray(evr.ao_enterprise_vessel_report_form_versions) &&
      isString(evr.ao_enterprise_workflow_vessel_id) &&
      isString(evr.created_at) &&
      isString(evr.hash) &&
      isString(evr.id) &&
      isString(evr.updated_at) &&
      isNumber(evr.version) &&
      isString(evr.workflow_status_id)
    );
  }

  /**
   * Retrieves the value from a FormVersionItem object.
   *
   * @param {AoEnterpriseVesselReportFormVersionItem} aoEnterpriseVesselReportFormVersionItem - The FormVersionItem object to retrieve the value from.
   * @returns {number|string|string[]|null} - The value retrieved from the FormVersionItem object. Returns null if the object is not valid.
   */
  getValueFromReportFormVersionItem(
    reportFormVersionItem // AoEnterpriseVesselReportFormVersionItem
  ) {
    const rFVI = reportFormVersionItem;

    if (!isObject(rFVI)) return null; // null
    if (!isNull(rFVI.value_number)) return rFVI.value_number; // number
    if (!isNull(rFVI.value_string)) return rFVI.value_string; // string
    if (!isNull(rFVI.value_date)) return rFVI.value_date; // string
    if (!isNull(rFVI.value_time)) return rFVI.value_time; // string
    if (!isNull(rFVI.value_date_time)) return rFVI.value_date_time; // string
    if (Array.isArray(rFVI.value_files) && rFVI.value_files.length > 0) {
      return rFVI.value_files; // string[]
    }
    if (hasValueJsonContent(rFVI.value_json)) return rFVI.value_json; // 14/15/17/18: object|array (value_json)
    return null; // null
  }

  /**
   * Checks if the given report form version item has a value.
   *
   * @param {AoEnterpriseVesselReportFormVersionItem} reportFormVersionItem - The report form version item to check.
   * @returns {boolean} - Returns true if the report form version item has a value, otherwise returns false.
   */
  hasReportFormVersionItemValue(
    reportFormVersionItem // AoEnterpriseVesselReportFormVersionItem
  ) {
    const value = this.getValueFromReportFormVersionItem(reportFormVersionItem);
    if (isNull(value)) return false;
    if (isString(value)) return value !== "";
    if (Array.isArray(value)) return value.length > 0;
    if (isObject(value)) return Object.keys(value).length > 0; // value_json (object)
    return isNumber(value);
  }

  /**
   * Checks if the nullable_checked property of the given reportFormVersionItem is true.
   *
   * @param {AoEnterpriseVesselReportFormVersionItem} reportFormVersionItem - The reportFormVersionItem to check.
   * @returns {boolean} - Returns true if nullable_checked is true, false otherwise.
   */
  isNullableChecked(
    reportFormVersionItem // AoEnterpriseVesselReportFormVersionItem
  ) {
    const rFVI = reportFormVersionItem;

    if (!isObject(rFVI)) return false;
    return rFVI.nullable_checked === true;
  }

  hasReportFormVersionItemFiles(
    reportFormVersionItem // AoEnterpriseVesselReportFormVersionItem
  ) {
    const rFVI = reportFormVersionItem;

    if (!isObject(rFVI)) return false;
    return Array.isArray(rFVI.value_files) && rFVI.value_files.length > 0;
  }

  /**
   * Retrieves the search conditions for generating operation reports.
   * @returns {Promise<Object>} A promise that resolves to an object containing the search conditions.
   */
  async getReportSearchConditions() {
    return await fireStoreService.getOperationSettings(
      OPERATIONS.REPORT_SEARCH_CONDITIONS_DOCUMENT_NAME
    );
  }

  async setReportSearchConditions(
    conditions = this.operationReportIndexRequestObject()
  ) {
    if (!isObject(conditions)) return;

    return await fireStoreService.setOperationSettings(
      OPERATIONS.REPORT_SEARCH_CONDITIONS_DOCUMENT_NAME,
      conditions
    );
  }

  /**
   * 直前レポート（同一 workflow vessel の最新レポート）を1件取得する。
   * 一覧ではフォーム項目の値が含まれない場合があるため、取得した id で show API を呼び直して完全なレポートを返す。
   *
   * @param {string} aoEnterpriseId - 運航企業ID
   * @param {string} aoEnterpriseWorkflowVesselId - 運航 workflow 船舶ID
   * @param {string} excludeReportId - 除外するレポートID（現在開いているレポート）
   * @returns {Promise<AoEnterpriseVesselReport|null>} 直前レポート、なければ null
   */
  async getLatestReportForWorkflowVessel(
    aoEnterpriseId,
    aoEnterpriseWorkflowVesselId,
    excludeReportId
  ) {
    if (!isString(aoEnterpriseId) || !isString(aoEnterpriseWorkflowVesselId)) {
      return null;
    }
    const params = this.operationReportIndexRequestObject({
      aoEnterpriseIds: [aoEnterpriseId],
      page: 0,
      size: 20,
    });
    const res = await this.getOperationReports(params);
    const data = res && res.data ? res.data : null;
    if (!data || !Array.isArray(data.ao_enterprise_vessel_reports)) {
      return null;
    }
    const sameVessel = data.ao_enterprise_vessel_reports.filter(
      r =>
        r.ao_enterprise_workflow_vessel_id === aoEnterpriseWorkflowVesselId &&
        r.id !== excludeReportId
    );
    const firstReport = sameVessel.length > 0 ? sameVessel[0] : null;
    if (!firstReport || !firstReport.id) return null;
    // 一覧は項目値を含まないことがあるため、show API で完全なレポートを取得する
    try {
      const detailRes = await this.getOperationReport({
        aoEnterpriseId,
        reportId: firstReport.id,
      });
      const fullReport = detailRes && detailRes.data ? detailRes.data : null;
      return fullReport;
    } catch {
      return null;
    }
  }

  /**
   * レポートから form_version_item_id -> 項目値 のマップを構築する。
   * LATEST デフォルト値の置き換えに使用。
   *
   * @param {AoEnterpriseVesselReport} report - 直前レポート
   * @returns {Object<string, AoEnterpriseVesselReportFormVersionItem>} form_version_item_id をキーにしたマップ
   */
  buildLatestReportValueMap(report) {
    if (
      !isObject(report) ||
      !Array.isArray(report.ao_enterprise_vessel_report_form_versions)
    ) {
      return {};
    }
    const map = {};
    for (const fv of report.ao_enterprise_vessel_report_form_versions) {
      const items = fv.ao_enterprise_vessel_report_form_version_items;
      if (!Array.isArray(items)) continue;
      for (const item of items) {
        if (!item) continue;
        const key = item.form_version_item_id;
        if (key != null) {
          map[String(key)] = item;
        }
      }
    }
    return map;
  }

  /**
   * 更新送信前に、value_string 等が "LATEST" のまま送られないようサニタイズする。
   * 初回レポートで直前がない場合のバグ防止。
   *
   * @param {Object} reportBody - レポート更新ボディ（ao_enterprise_vessel_report_form_versions を含む）
   * @returns {Object} サニタイズされたコピー
   */
  sanitizeReportBodyForUpdate(reportBody) {
    if (!isObject(reportBody)) return reportBody;
    const versions = reportBody.ao_enterprise_vessel_report_form_versions;
    if (!Array.isArray(versions)) return reportBody;
    const sanitizedVersions = versions.map(fv => {
      const items = fv.ao_enterprise_vessel_report_form_version_items;
      if (!Array.isArray(items)) return fv;
      const sanitizedItems = items.map(item => {
        if (!isObject(item)) return item;
        const next = { ...item };
        if (
          isString(next.value_string) &&
          next.value_string.toUpperCase() === "LATEST"
        ) {
          next.value_string = null;
        }
        return next;
      });
      return {
        ...fv,
        ao_enterprise_vessel_report_form_version_items: sanitizedItems,
      };
    });
    return {
      ...reportBody,
      ao_enterprise_vessel_report_form_versions: sanitizedVersions,
    };
  }
}

export default new OperationReportService();
